import { Reservation } from '../reservation.model';
import { SegmentInt } from '@interfaces/*';
import { Passenger } from '../passenger.model';
import {
  CHECKIN_STATUS,
  FLIGHT_ITINERARY_TYPE,
  FLIGHT_ITINERARY_TYPE_MICRO,
  PAX_TYPE,
  PAX_TYPE_SHORT,
} from '@app/enums';
import { Mandate, PassengerIds, Trip } from '@models/micro/response/viewDocuments.model';
import { FlightSummary } from '../flight-summary.model';
import { Segment } from '../segment.model';
import { PassengerIdMapService } from '@app/services/passenger-id-map.service';
import { normalizeAncillariesStatus } from '@utils/functions/ancillaries.utils';
export class RequestPecTab {
  mandates: Mandate[] = [];
  reservationCode: string;
  travelPartyId = '';
  trips: Trip[];
  tripType: string;
  bookedLegCollection: BookedLegCollectionRQ[];
  passengers: PassengersRQ[];
  seats = [];
  ancillaries = [];
  reprintBP: boolean = false;
  passengerNumbers = 0;
  isStandBy: boolean = false;

  constructor(
    reservation: Reservation,
    reprintBp: boolean,
    passengerIdMapService?: PassengerIdMapService
  ) {
    const flightType = {
      [FLIGHT_ITINERARY_TYPE.ONEWAY]: FLIGHT_ITINERARY_TYPE_MICRO.ONEWAY,
      [FLIGHT_ITINERARY_TYPE.ROUNDTRIP]: FLIGHT_ITINERARY_TYPE_MICRO.ROUNDTRIP,
      [FLIGHT_ITINERARY_TYPE.MULTICITY]: FLIGHT_ITINERARY_TYPE_MICRO.MULTICITY,
    };

    this.reservationCode = reservation.pnr;
    this.tripType = flightType[reservation.flightItineraryType];
    this.bookedLegCollection = [new BookedLegCollectionRQ(reservation.flightSummary)];
    const allPassengers = reservation.passengers || [];

    let filteredPassengers = reprintBp
      ? allPassengers.filter((p) => p.checkinSelected && p.checkinStatus)
      : allPassengers.filter((p) => p.checkinSelected && !p.checkinStatus);

    if (!filteredPassengers.length) {
      filteredPassengers = allPassengers.filter((p) => p.checkinSelected);
    }

    if (!filteredPassengers.length) {
      filteredPassengers = allPassengers;
    }

    if (reprintBp) {
      this.reprintBP = reprintBp;
    }

    this.passengers = filteredPassengers.map(
      (passenger) => new PassengersRQ(passenger, reservation, passengerIdMapService)
    );

    if (reservation.codeShare) {
      this.travelPartyId = reservation.travelPartyId;
      this.trips = reservation.trips;
      const confirmed = { confirmed: true };
      this.mandates =
        reservation.mandates?.map((mandate) => {
          return Object.assign(mandate, confirmed);
        }) || [];
    }
    this.passengerNumbers = reservation.passengers.length;
    this.isStandBy = reservation.isStandBy;
  }
}

export class BookedLegCollectionRQ {
  codeShare = false;
  legCode: string;
  legEntity: string;
  legRegion: string;
  selectedLeg = true;
  segments: SegmentsRQ[];
  sdsProvider: string;

  constructor(leg: FlightSummary) {
    this.legCode = leg?.legCode;
    this.legEntity = leg?.legEntity;
    this.legRegion = leg?.legRegion;
    this.sdsProvider = leg.sdsProvider;
    this.segments =
      leg.segments
        .filter((segment) => segment.windowSegmentStatus == 'CKI')
        ?.map((segment) => new SegmentsRQ(segment)) || [];
    // `flightSummary.codeShare` solo se asigna en el lookup con viewDocuments, asi
    // que en flujos que reemplazan el flightSummary (p.ej. el refresh posterior al
    // pago del upgrade) llega undefined y JSON.stringify elimina la llave del
    // request, lo que el servicio rechaza con ERR.RC.CHECKIN.BAD_REQUEST.
    this.codeShare = leg?.codeShare ?? false;
  }
}

export class SegmentsRQ {
  arrivalAirport: string;
  bookingClass: string;
  departureAirport: string;
  departureDateTime: string;
  operatingCarrier: string;
  operatingFlightCode: string;
  segmentCode: string;
  segmentStatusCode: string;
  departureAirportTimeZoneId: string;
  segmentEntity: string;
  segmentRegion: string;
  farebasis: string;
  windowSegmentStatus: string;

  constructor(segment: Segment) {
    this.arrivalAirport = segment?.arrivalAirport;
    this.segmentCode = segment?.segmentCode;
    this.departureAirport = segment?.departureAirport;
    this.departureDateTime = segment?.departureDateTime;
    this.operatingCarrier = segment?.operatingCarrier;
    this.operatingFlightCode = segment?.operatingFlightCode;
    this.segmentStatusCode = segment?.segmentStatusCode;
    this.bookingClass = segment?.bookingClass;
    this.departureAirportTimeZoneId = segment?.departureAirportTimeZoneId;
    this.segmentEntity = segment?.segmentEntity;
    this.segmentRegion = segment?.segmentRegion;
    this.farebasis = segment?.farebasis;
    this.windowSegmentStatus = segment?.windowSegmentStatus || null;
  }
}

export class PassengersRQ {
  isEmployee = false;
  id: string;
  priorityCode: string = null;
  upgradeCode: string = null;
  selectedForCheckin = true;
  firstName: string;
  lastName: string;
  passengerNameNumber: string;
  gender: string;
  paxType: string;
  passengerIds: PassengerIds[];
  isStandBy = false;
  seats = [];
  ancillaries = [];
  fareFamily = '';
  //seamless
  ticketNumber: string;
  passengerIdSkyteam = '';
  passportScanned = false;

  constructor(
    passenger: Passenger,
    reservation?: Reservation,
    passengerIdMapService?: PassengerIdMapService
  ) {
    const paxType = {
      [PAX_TYPE.ADULT]: PAX_TYPE_SHORT.ADULT,
      [PAX_TYPE.CHILD]: PAX_TYPE_SHORT.CHILD,
      [PAX_TYPE.INFANT]: PAX_TYPE_SHORT.INFANT,
    };
    const priorityCode = passenger?.priorityCode;
    this.id = passenger?.passengerId;
    this.priorityCode = priorityCode || null;
    this.ticketNumber = passenger?.ticketInfo?.eticket;
    this.upgradeCode = passenger?.upgradeCode || null;
    this.passengerNameNumber = passenger?.nameRefNumber;
    this.firstName = passenger?.firstName;
    this.lastName = passenger?.lastName;
    this.gender = passenger?.gender?.substring(0, 1);
    this.paxType = paxType[passenger.paxType];

    const checkInSegmentCodes: string[] =
      reservation?.flightSummary?.segments
        ?.filter((segment: Segment) => segment.windowSegmentStatus === CHECKIN_STATUS.CKI)
        .map((segment: Segment) => segment.segmentCode) ?? [];

    // Un asiento solo puede viajar en el request si cumple dos condiciones, o el
    // servicio responde ERR.RC.CHECKIN.BAD_REQUEST:
    // 1. Pertenece a un segmento que se esta haciendo check-in. `bookedLegCollection`
    //    tambien se filtra por CKI, igual que `passengerIds` mas abajo, asi que un
    //    asiento de otro segmento deja el request incoherente.
    // 2. Proviene del backend (shape EMD, reconocible por `groupCode`). El seatmap
    //    escribe en `passenger.seats` su modelo de vista (`_meta`, `code` con el
    //    numero de asiento, `status` numerico, `currency` con `taxDetails`) y hay
    //    rutas que lo dejan ahi: si `setSeat` falla,
    //    `openSeatAssignmentFailureModal` conserva deliberadamente los asientos
    //    ASSIGNED/PAID, que son precisamente esos objetos de vista.
    this.seats = (passenger.seats ?? []).filter(
      (seat) =>
        (!checkInSegmentCodes.length || checkInSegmentCodes.includes(seat.segmentCode)) &&
        'groupCode' in seat
    );
    this.ancillaries = normalizeAncillariesStatus(passenger.ancillaries);
    this.passportScanned = passenger?.passengerInfo?.passport?.passportScanned || false;

    if (reservation) {
      const { isEmployee, isStandBy } = reservation;
      this.isEmployee = isEmployee;
      this.isStandBy = isStandBy;
    }

    this.fareFamily = reservation?.flightSummary?.fareFamily || '';

    const idsForCheckInLeg: PassengerIds[] = (passenger?.passengerIds ?? []).filter(
      (data: PassengerIds) => checkInSegmentCodes.includes(data.segmentCode)
    );

    this.passengerIds =
      idsForCheckInLeg.length > 0
        ? idsForCheckInLeg.map((data: PassengerIds) => new PassengerIds(data))
        : passenger?.passengerIds?.map((data) => new PassengerIds(data)) || [];
    this.passengerIdSkyteam = passenger?.passengerIdSkyteam;

    // Fallback from PassengerIdMapService when passengerId or passengerIds are missing
    if (passengerIdMapService && passenger?.nameRefNumber) {
      const mapped = passengerIdMapService.get(passenger.nameRefNumber);
      if (mapped) {
        if (!this.id) {
          this.id = mapped.passengerId;
        }
        if (!this.passengerIds || this.passengerIds.length === 0) {
          this.passengerIds = mapped.passengerIds?.map((data) => new PassengerIds(data)) || [];
        }
      }
    }
  }
}
